'use client';

import { PropsWithChildren, ReactElement, useEffect } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { colors } from '@/colors';
import { Container } from '@/components/Container';
import { RoundBorderContainer } from '@/components/RoundBorderContainer';
import { IAccordionKeyName } from '@/types/IAccordionKeyName';
import { getLocalStorageKey } from '@/utils/getLocalStorageKey';

interface IAccordionContainerProps extends PropsWithChildren {
  keyName: IAccordionKeyName;
  visibleTitle: string | ReactElement;
  useMininimalisticBorder?: boolean;
  onOpen?: () => void | Promise<void>;
}

export const AccordionContainer = ({
  useMininimalisticBorder = false,
  onOpen,
  visibleTitle,
  children,
  keyName,
}: IAccordionContainerProps) => {
  const [isOpen, setIsOpen] = useLocalStorage<boolean>(getLocalStorageKey(`${keyName}_OPEN`), true);
  useEffect(() => {
    if (isOpen && onOpen) {
      onOpen();
    }
  }, [isOpen, onOpen]);
  const OuterContainer = useMininimalisticBorder ? Container : RoundBorderContainer;
  return (
    <OuterContainer>
      <Container
        className={'accordion-header'}
        onClick={() => {
          setIsOpen((prevState) => !prevState);
        }}
        sx={{
          alignItems: 'center',
          padding: '8px 16px',
          fontWeight: 'bold',
          display: 'flex',
          flexDirection: 'row',
          borderBottom: useMininimalisticBorder ? '1px solid #c3c3c3' : undefined,
          backgroundColor: colors.grayLightest,
        }}
      >
        <Container
          className={'accordion-header-title'}
          sx={{
            cursor: 'pointer',
            textAlign: 'left',
            flex: 1,
            fontWeight: 'bold',
          }}
        >
          {visibleTitle}
        </Container>
        <Container
          className={'accordion-header-arrow-button'}
          sx={{
            cursor: 'pointer',
            flex: 0,
            fontSize: '10px',
            transform: `rotate(${isOpen ? '360deg' : '180deg'})`,
            transition: 'all 0.5s !important',
            backgroundColor: '#d1d1d1',
            height: 32,
            width: 32,
            flexBasis: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          /* eslint-disable-next-line no-hardcoded-strings/no-hardcoded-strings */
        >
          &#9650;
        </Container>
      </Container>
      <Container
        sx={{
          overflow: 'hidden',
          height: isOpen ? 'auto' : 0,
          opacity: isOpen ? 1 : 0,
          transition: 'opacity 0.5s 0.5',
        }}
      >
        {children}
      </Container>
    </OuterContainer>
  );
};
